import React, { useEffect, useId, useMemo, useState } from "react";
import { FiMaximize2, FiMinimize2 } from "react-icons/fi";
import TimeSeriesPlot from "./TimeSeriesPlot";
import ChartNavigator from "./ChartNavigator";
import type { ChartSpec } from "./types";
import { legacyIndicatorDataToChartSpec } from "./legacyAdapter";
import { validateChartSpec } from "./validateChartSpec";
import { chartTimeMilliseconds } from "./timeRange";
import { NAVIGATOR_MIN_POINTS } from "./displayDensity";
import "./UnifiedChart.css";

const MaximizeIcon = FiMaximize2 as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const MinimizeIcon = FiMinimize2 as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;

type UnifiedChartProps = {
  spec?: ChartSpec;
  sourceLabel?: string;
  observedAt?: string;
  height?: number;
  symbolSelector?: React.ReactNode;
  [key: string]: unknown;
};

function formatLatestValue(value: unknown, format: 'number' | 'compact' | 'currency' | 'percent' | 'integer'): string {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  if (format === 'currency') return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(number);
  if (format === 'percent') return `${number.toFixed(2)}%`;
  if (format === 'compact') return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(number);
  if (format === 'integer') return Math.round(number).toLocaleString();
  return number.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export default function UnifiedChart({
  spec: explicitSpec,
  sourceLabel,
  observedAt,
  height = 520,
  symbolSelector,
  ...legacyProps
}: UnifiedChartProps): React.ReactElement {
  const chartSyncId = useId();
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const [visibleRange, setVisibleRange] = useState<[number, number]>([0, 0]);
  const [scaleMode, setScaleMode] = useState<'linear' | 'log' | 'percentage' | 'indexed'>('linear');
  const spec = useMemo(
    () =>
      explicitSpec ||
      legacyIndicatorDataToChartSpec(
        Array.isArray(legacyProps.indicatorData)
          ? (legacyProps.indicatorData as never[])
          : [],
        Array.isArray(legacyProps.signalData)
          ? (legacyProps.signalData as never[])
          : [],
      ),
    [explicitSpec, legacyProps.indicatorData, legacyProps.signalData],
  );
  const errors = useMemo(() => (spec ? validateChartSpec(spec) : []), [spec]);
  const renderedSpec = useMemo(() => {
    if (!spec || (scaleMode !== 'percentage' && scaleMode !== 'indexed')) return spec;
    const baselines = new Map<string, number>();
    spec.series.forEach((series) => {
      if (series.role === 'volume' || series.placement === 'price-volume-overlay') return;
      if (!series.dataKey) return;
      const first = spec.data.find((row) => {
        const candidate = row[series.dataKey!];
        return candidate !== null && candidate !== undefined && candidate !== "" && Number.isFinite(Number(candidate));
      })?.[series.dataKey];
      const value = Number(first);
      if (Number.isFinite(value) && value !== 0) baselines.set(series.dataKey, value);
    });
    return {
      ...spec,
      data: spec.data.map((row) => {
        const next = { ...row };
        baselines.forEach((baseline, key) => {
          const value = Number(row[key]);
          if (row[key] === null || row[key] === undefined || row[key] === "" || !Number.isFinite(value)) return;
          next[key] = scaleMode === 'indexed' ? (value / baseline) * 100 : ((value / baseline) - 1) * 100;
        });
        return next;
      }),
      axes: spec.axes.map((axis) => {
        const isVolumeOverlayAxis = spec.series.some((series) =>
          series.axisId === axis.id && series.placement === 'price-volume-overlay'
        );
        return isVolumeOverlayAxis
          ? axis
          : {
              ...axis,
              format: scaleMode === 'percentage' ? 'percent' as const : 'number' as const,
            };
      }),
    };
  }, [scaleMode, spec]);

  useEffect(() => {
    const lastIndex = Math.max(0, (spec?.data.length || 1) - 1);
    setActiveIndex(lastIndex);
    setVisibleRange([0, lastIndex]);
  }, [spec]);

  if (!spec) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload has no explicit series data.</p>
      </section>
    );
  }

  if (errors.length > 0) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload is missing required structural metadata.</p>
        <ul>
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </section>
    );
  }

  const totalWeight = spec.panes.reduce((sum, pane) => sum + pane.weight, 0);
  const plotHeight = expanded
    ? Math.max(window.innerHeight - 190, 600)
    : height;
  const controls = spec.controls || {};
  const latestRow = spec.data.at(-1);
  const latestSeries = spec.profile === 'financial'
    ? []
    : spec.series.filter((series) => series.showLastValue && series.dataKey);
  const markerLegend = [...(spec.markers || []).reduce((groups, marker) => {
    if (!marker.compact) return groups;
    const existing = groups.get(marker.label);
    groups.set(marker.label, existing
      ? { ...existing, count: existing.count + 1 }
      : {
          label: marker.label,
          color: marker.color,
          shape: marker.shape,
          count: 1,
        });
    return groups;
  }, new Map<string, { label: string; color: string; shape: string; count: number }>()).values()];

  const chooseRange = (preset: string) => {
    const last = spec.data.length - 1;
    if (preset === 'ALL' || spec.xAxis.type !== 'time') {
      setVisibleRange([0, last]);
      return;
    }
    const lastTime = chartTimeMilliseconds(spec.data[last][spec.xAxis.key]);
    if (lastTime === null) return;
    const dayCounts: Record<string, number> = {
      '1D': 1,
      '5D': 5,
      '1M': 31,
      '3M': 93,
      '6M': 186,
      '1Y': 366,
      '3Y': 365 * 3,
      '5Y': 365 * 5,
    };
    const dayCount = dayCounts[preset] ?? 366;
    const threshold = preset === 'YTD'
      ? Date.UTC(new Date(lastTime).getUTCFullYear(), 0, 1)
      : lastTime - dayCount * 86_400_000;
    const start = Math.max(0, spec.data.findIndex((row) => {
      const value = chartTimeMilliseconds(row[spec.xAxis.key]);
      return value !== null && value >= threshold;
    }));
    setVisibleRange([start, last]);
  };

  const zoom = (direction: 'in' | 'out') => {
    const [start, end] = visibleRange;
    const span = end - start + 1;
    const delta = Math.max(1, Math.floor(span * 0.2));
    if (direction === 'in' && span > 4) setVisibleRange([start + Math.floor(delta / 2), end - Math.ceil(delta / 2)]);
    if (direction === 'out') setVisibleRange([Math.max(0, start - delta), Math.min(spec.data.length - 1, end + delta)]);
  };

  const navigatorValues = spec.data.map((row) => {
    const price = spec.series.find((item) => item.role === "price") || spec.series[0];
    if (price?.mark === "candlestick" && price.fieldMapping) {
      const value = Number(row[price.fieldMapping.close]);
      return Number.isFinite(value) ? value : null;
    }
    if (!price?.dataKey) return null;
    const value = Number(row[price.dataKey]);
    return Number.isFinite(value) ? value : null;
  });
  const showNavigator = spec.kind === "timeseries"
    && controls.allowZoom !== false
    && spec.data.length >= NAVIGATOR_MIN_POINTS;

  const pan = (direction: 'left' | 'right') => {
    const [start, end] = visibleRange;
    const shift = Math.max(1, Math.floor((end - start + 1) * 0.2));
    if (direction === 'left') {
      const nextStart = Math.max(0, start - shift);
      setVisibleRange([nextStart, nextStart + (end - start)]);
    } else {
      const nextEnd = Math.min(spec.data.length - 1, end + shift);
      setVisibleRange([nextEnd - (end - start), nextEnd]);
    }
  };

  return (
    <section
      className={`unified-chart ${expanded ? "unified-chart--expanded" : ""}`}
      data-testid="unified-chart"
      data-chart-kind={spec.kind}
      data-chart-profile={spec.profile || 'analytical'}
      data-chart-row-count={spec.data.length}
      aria-label={spec.title}
    >
      <header className="unified-chart__header">
        <div className="unified-chart__header-copy">
          <h4>{spec.title}</h4>
          {spec.profile !== 'financial' && spec.subtitle && (
            <p className="unified-chart__subtitle">{spec.subtitle}</p>
          )}
          {spec.explanation && (
            <details
              className="unified-chart__details"
              data-testid="chart-details"
            >
              <summary>About this chart</summary>
              <p className="unified-chart__explanation">{spec.explanation}</p>
            </details>
          )}
        </div>
        <div className="unified-chart__header-tools">
          {symbolSelector ? (
            <div className="unified-chart__symbol-selector" data-testid="chart-symbol-selector">
              {symbolSelector}
            </div>
          ) : null}
          <button
            type="button"
            className="unified-chart__expand"
            onClick={() => setExpanded((value) => !value)}
            aria-label={expanded ? "Exit full screen chart" : "Expand chart"}
          >
            {expanded ? (
              <MinimizeIcon aria-hidden />
            ) : (
              <MaximizeIcon aria-hidden />
            )}
          </button>
        </div>
      </header>

      {latestSeries.length > 0 && latestRow && (
        <div className="unified-chart__latest-values" aria-label="Latest chart values">
          {latestSeries.map((series) => {
            const axis = spec.axes.find((candidate) => candidate.id === series.axisId);
            return (
              <span key={series.id}>
                <strong>{series.label}</strong>{' '}
                {formatLatestValue(latestRow[series.dataKey!], axis?.format || 'number')}
              </span>
            );
          })}
        </div>
      )}

      {markerLegend.length > 0 && (
        <div className="unified-chart__marker-legend" aria-label="Trade markers" data-testid="trade-marker-legend">
          {markerLegend.map((item) => (
            <span key={item.label} data-marker-legend-item={item.label}>
              <i
                className={`unified-chart__marker-symbol unified-chart__marker-symbol--${item.shape}`}
                style={{ color: item.color }}
                aria-hidden
              >
                {item.shape === 'arrowUp' ? '↑' : item.shape === 'arrowDown' ? '↓' : item.shape === 'square' ? '■' : '●'}
              </i>
              {item.label}
              <strong>{item.count.toLocaleString()}</strong>
            </span>
          ))}
        </div>
      )}

      <div className="unified-chart__controls" aria-label="Chart controls">
        {(controls.rangePresets || []).map((preset) => (
          <button key={preset} type="button" onClick={() => chooseRange(preset)}>{preset}</button>
        ))}
        {controls.allowZoom !== false && (
          <span className="unified-chart__control-group" aria-label="Zoom controls">
            <button type="button" onClick={() => zoom('out')} aria-label="Zoom out">−</button>
            <button type="button" onClick={() => zoom('in')} aria-label="Zoom in">+</button>
          </span>
        )}
        {controls.allowPan !== false && (
          <span className="unified-chart__control-group" aria-label="Pan controls">
            <button type="button" onClick={() => pan('left')} aria-label="Pan left">←</button>
            <button type="button" onClick={() => pan('right')} aria-label="Pan right">→</button>
          </span>
        )}
        {(controls.scaleModes || []).map((mode) => (
          <button key={mode} type="button" className={scaleMode === mode ? 'is-active' : ''} onClick={() => setScaleMode(mode)}>{mode}</button>
        ))}
      </div>

      {controls.allowSeriesToggle !== false && spec.series.length > 1 && (
        <div className="unified-chart__series-toggles" aria-label="Chart series visibility">
          {spec.series.map((series) => (
            <button
              key={series.id}
              type="button"
              className={hiddenSeries.has(series.id) ? 'is-hidden' : ''}
              onClick={() => setHiddenSeries((current) => {
                const next = new Set(current);
                if (next.has(series.id)) next.delete(series.id); else next.add(series.id);
                return next;
              })}
            >
              <i style={{ backgroundColor: series.style?.color }} aria-hidden />
              {series.label}
            </button>
          ))}
        </div>
      )}

      <div className="unified-chart__plot" style={{ height: plotHeight }}>
        {spec.panes.map((pane, index) => (
          <TimeSeriesPlot
            key={pane.id}
            syncId={chartSyncId}
            spec={renderedSpec || spec}
            pane={pane}
            height={(plotHeight * pane.weight) / totalWeight}
            showXAxis={index === spec.panes.length - 1}
            visibleRange={visibleRange}
            activeIndex={activeIndex}
            hiddenSeries={hiddenSeries}
            scaleMode={scaleMode}
            onHover={setActiveIndex}
          />
        ))}
      </div>

      {showNavigator && (
        <ChartNavigator
          pointCount={spec.data.length}
          visibleRange={visibleRange}
          values={navigatorValues}
          onChange={setVisibleRange}
        />
      )}

      {(sourceLabel || observedAt || spec.notes) && (
        <footer className="unified-chart__footer">
          {sourceLabel && <span>{sourceLabel}</span>}
          {observedAt && <span>{new Date(observedAt).toLocaleString()}</span>}
          {spec.notes && <span>{spec.notes}</span>}
        </footer>
      )}
    </section>
  );
}
